import { useState } from "react";
import { Pencil, Power } from "lucide-react";
import type { Plano } from "@/api/contrato";
import { useSessao } from "@/sessao/useSessao";
import { useAlterarPlano, usePlano } from "@/api/consultas";
import { textoDoErro } from "@/api/problema";
import { PainelLateral } from "@/componentes/PainelLateral";
import { CaixaDeCampo } from "@/componentes/CaixaDeCampo";
import { Botao } from "@/componentes/Botao";
import { Pilula } from "@/componentes/PilulaDeSituacao";
import { useAvisos } from "@/componentes/Avisos";
import { Confirmacao } from "@/componentes/Confirmacao";
import { Esqueleto } from "@/componentes/Esqueleto";
import { FaixaDeErro } from "@/componentes/FaixaDeErro";
import { useDetalheDaRota } from "@/componentes/useDetalheDaRota";
import { dia, dinheiro, quantidade } from "@/formato";
import { ROTA_DOS_PLANOS } from "@/rotas";
import { EditarPlano } from "./EditarPlano";
import { franquia, rotuloDaPolitica, usaExcedente } from "./plano";
import { MATIZ_DA_SITUACAO, nomeDoContratante } from "./situacao";

export function DetalheDoPlano({
  planoID,
  planoDaLista,
  aoFechar,
}: {
  planoID: string;
  /** A linha da lista, para o painel não piscar. Link colado pode não a ter. */
  planoDaLista?: Plano | undefined;
  aoFechar: () => void;
}) {
  const { cliente, pode } = useSessao();
  const avisos = useAvisos();

  const { data: detalhe, error } = usePlano(cliente, planoID);
  const plano = detalhe?.plano ?? planoDaLista;
  const contratos = detalhe?.contratos ?? [];

  const alterar = useAlterarPlano(cliente, planoID);
  const [editando, setEditando] = useState(false);
  const [desativando, setDesativando] = useState(false);

  if (plano === undefined) {
    return (
      <PainelLateral
        aberto
        aoFechar={aoFechar}
        descricao="Detalhe do plano"
        titulo={
          error ? (
            <span className="font-semibold text-texto">Plano</span>
          ) : (
            <Esqueleto className="h-5 w-40" />
          )
        }
      >
        <div aria-busy={!error} className="p-4">
          {error ? <FaixaDeErro erro={error} /> : <Esqueleto className="h-40" />}
        </div>
      </PainelLateral>
    );
  }

  const ehAdmin = pode("admin");
  const comExcedente = usaExcedente(plano.politica_excedente);

  function reativar() {
    alterar.mutate(
      { disponivel: true },
      {
        onSuccess: () => avisos.feito("Plano reativado"),
        onError: (falha) => avisos.falhou(textoDoErro(falha)),
      },
    );
  }

  return (
    <>
      <PainelLateral
        aberto
        aoFechar={aoFechar}
        descricao={`Plano ${plano.nome}`}
        titulo={
          <div className="flex flex-col gap-1">
            <span className="truncate text-titulo font-semibold text-texto">{plano.nome}</span>
            <span className="dados text-[12px] text-texto-2">
              {dinheiro(plano.preco_base_centavos)}/mês · {franquia(plano.limite_mensal)} ·{" "}
              {plano.disponivel ? "disponível" : "indisponível"}
            </span>
          </div>
        }
        {...(ehAdmin ? {
          rodape: (
            <div className="flex flex-wrap gap-2">
              <Botao tom="secundario" onClick={() => setEditando(true)}>
                <Pencil aria-hidden="true" className="size-4" />
                Editar
              </Botao>

              {plano.disponivel ? (
                <Botao tom="destrutivo" onClick={() => setDesativando(true)}>
                  <Power aria-hidden="true" className="size-4" />
                  Desativar
                </Botao>
              ) : (
                <Botao tom="secundario" carregando={alterar.isPending} onClick={reativar}>
                  <Power aria-hidden="true" className="size-4" />
                  Reativar
                </Botao>
              )}
            </div>
          ),
        } : {})}
      >
        {error ? (
          <p role="alert" className="px-4 py-6 text-falha">
            {textoDoErro(error)}
          </p>
        ) : (
          <div className="flex flex-col gap-4 p-4">
            <div className="grid grid-cols-2 gap-2">
              {plano.descricao !== null && (
                <CaixaDeCampo rotulo="Descrição" className="col-span-2">
                  {plano.descricao}
                </CaixaDeCampo>
              )}
              <CaixaDeCampo rotulo="Mensalidade" dados>
                {dinheiro(plano.preco_base_centavos)}
              </CaixaDeCampo>
              <CaixaDeCampo rotulo="Franquia" dados>
                {franquia(plano.limite_mensal)}
              </CaixaDeCampo>
              <CaixaDeCampo rotulo="Política">{rotuloDaPolitica(plano.politica_excedente)}</CaixaDeCampo>
              <CaixaDeCampo rotulo="Excedente" dados>
                {comExcedente ? `${dinheiro(plano.preco_excedente_centavos)} cada` : null}
              </CaixaDeCampo>
              <CaixaDeCampo rotulo="Teto de excedente" dados>
                {/*
                  Teto AUSENTE é ilimitado, e não zero: zero é "nenhum
                  excedente", o contrário.
                */}
                {!comExcedente
                  ? null
                  : plano.teto_excedente === null
                    ? "ilimitado"
                    : quantidade(plano.teto_excedente)}
              </CaixaDeCampo>
              <CaixaDeCampo rotulo="Requisições por segundo" dados>
                {plano.limite_rps ?? "padrão"}
              </CaixaDeCampo>
            </div>

            <section className="flex flex-col gap-2">
              <h3 className="rotulo">Contratos</h3>

              {contratos.length === 0 ? (
                <p className="text-texto-2">Nenhum contrato.</p>
              ) : (
                <ul className="flex flex-col gap-1">
                  {contratos.map((contrato) => (
                    <li
                      key={contrato.id}
                      className="flex items-center justify-between gap-2 rounded-controle border border-fio bg-superficie-2 px-3 py-2"
                    >
                      <span className="min-w-0 truncate text-texto">{nomeDoContratante(contrato)}</span>
                      <span className="dados shrink-0 text-[12px] text-texto-2">
                        {dia(contrato.inicio)} → {contrato.fim === null ? "aberto" : dia(contrato.fim)}
                      </span>
                      <Pilula matiz={MATIZ_DA_SITUACAO[contrato.situacao]}>{contrato.situacao}</Pilula>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>
        )}
      </PainelLateral>

      {editando && (
        <EditarPlano plano={plano} contratos={contratos} aoFechar={() => setEditando(false)} />
      )}

      <Confirmacao
        pedido={
          desativando
            ? {
                severidade: "leve",
                titulo: `Desativar ${plano.nome}?`,
                corpo: "Contrato novo deixa de aceitá-lo; os vigentes continuam.",
                verbo: "Desativar plano",
              }
            : null
        }
        aoCancelar={() => setDesativando(false)}
        aoConfirmar={async () => {
          await alterar.mutateAsync({ disponivel: false });
          setDesativando(false);
          avisos.feito("Plano desativado");
        }}
      />
    </>
  );
}

/**
 * `/planos/:id`: o painel sobre a lista. O `key` remonta o detalhe ao trocar de plano: o
 * estado de edição de um não pode vazar para o seguinte.
 */
export function PlanoDaRota() {
  const { id, linha, fechar } = useDetalheDaRota<Plano>(() => ROTA_DOS_PLANOS);

  return <DetalheDoPlano key={id} planoID={id} planoDaLista={linha} aoFechar={fechar} />;
}
